import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CrosswordContent, CrosswordEntry } from "../content-types";
import { normaliseLetterGrid } from "../crossword-norm";
import { Explanation, Prompt } from "./shared";
import type { QuestionRendererProps } from "./types";

/** Themed crossword: a vertical theme word with horizontal entries each crossing
 *  it at one shared letter. The grid fills live as the learner types; grading is
 *  all-or-nothing (every entry's folded answer matches). Mirrors iOS CrosswordView. */
export function CrosswordRenderer({ content, answer, setAnswer, revealed }: QuestionRendererProps) {
  const { t } = useTranslation();
  const c = content as unknown as CrosswordContent;
  const theme = c.theme_word ?? "";
  const entries: CrosswordEntry[] = c.entries ?? [];
  const typed = (answer as Record<string, string>) ?? {};

  const sorted = useMemo(
    () => [...entries].sort((a, b) => a.row_offset - b.row_offset),
    [entries],
  );
  // Theme column = largest cross index, so every entry starts at a non-negative col.
  const themeColumn = useMemo(
    () => entries.reduce((m, e) => Math.max(m, e.cross_letter_index), 0),
    [entries],
  );
  const totalCols = useMemo(() => {
    let maxEnd = themeColumn + 1;
    for (const e of entries) {
      maxEnd = Math.max(maxEnd, themeColumn - e.cross_letter_index + e.answer.length);
    }
    return maxEnd;
  }, [entries, themeColumn]);
  const rows = theme.length;

  const setFor = (id: string, raw: string, maxLen: number) => {
    const cleaned = raw.toUpperCase().replace(/[^\p{L}]/gu, "").slice(0, maxLen);
    setAnswer({ ...typed, [id]: cleaned });
  };

  const entryAt = (row: number, col: number): CrosswordEntry | undefined =>
    sorted.find(
      (e) =>
        e.row_offset === row &&
        col >= themeColumn - e.cross_letter_index &&
        col < themeColumn - e.cross_letter_index + e.answer.length,
    );

  const letterAt = (s: string, i: number): string =>
    i >= 0 && i < s.length ? s[i] : "";

  function cellContent(row: number, col: number) {
    const themeLetter = letterAt(theme, row);
    const onTheme = col === themeColumn;
    const entry = entryAt(row, col);
    if (!entry) {
      if (!onTheme) return null; // empty
      return { letter: themeLetter, kind: "themeOnly" as const, ok: null as boolean | null };
    }
    const entryColIndex = col - (themeColumn - entry.cross_letter_index);
    const isCrossing = col === themeColumn;
    const folded = normaliseLetterGrid(typed[entry.id] ?? "");
    let letter: string;
    let ok: boolean | null = null;
    if (revealed) {
      letter = letterAt(entry.answer, entryColIndex);
      ok = folded === entry.answer;
    } else if (isCrossing) {
      letter = themeLetter;
    } else {
      letter = letterAt(folded, entryColIndex);
    }
    return { letter, kind: isCrossing ? ("crossing" as const) : ("entry" as const), ok };
  }

  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-indigo-500">
        {t("Crossword")}
      </div>
      <Prompt>{c.prompt}</Prompt>

      {/* Grid */}
      <div className="my-4 flex justify-center">
        <div
          className="grid gap-0.5"
          style={{ gridTemplateColumns: `repeat(${totalCols}, 1.75rem)` }}
        >
          {Array.from({ length: rows }).flatMap((_, row) =>
            Array.from({ length: totalCols }).map((__, col) => {
              const cell = cellContent(row, col);
              const key = `${row}-${col}`;
              if (!cell) return <div key={key} className="h-7 w-7" />;
              const base =
                "flex h-7 w-7 items-center justify-center rounded font-mono text-sm font-semibold";
              if (cell.kind === "themeOnly" || cell.kind === "crossing") {
                return (
                  <div
                    key={key}
                    className={`${base} border border-indigo-400/60 bg-indigo-100 text-indigo-600`}
                  >
                    {cell.letter}
                  </div>
                );
              }
              const tone = revealed
                ? cell.ok
                  ? "border-emerald-400 bg-emerald-50 text-emerald-800"
                  : "border-red-400 bg-red-50 text-red-700"
                : "border-black/10 bg-slate-50 text-ink";
              return (
                <div key={key} className={`${base} border ${tone}`}>
                  {cell.letter}
                </div>
              );
            }),
          )}
        </div>
      </div>

      {/* Clues */}
      <div className="space-y-2.5">
        <div className="text-sm font-semibold text-ink">{t("Clues")}</div>
        {sorted.map((entry, idx) => {
          const folded = normaliseLetterGrid(typed[entry.id] ?? "");
          const ok = folded === entry.answer;
          return (
            <div key={entry.id} className="rounded-xl bg-slate-50 p-2.5">
              <div className="flex items-start gap-1.5 text-sm">
                <span className="font-bold text-indigo-500">{idx + 1}.</span>
                <span className="text-ink">{entry.clue}</span>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <input
                  type="text"
                  value={typed[entry.id] ?? ""}
                  disabled={revealed}
                  onChange={(e) => setFor(entry.id, e.target.value, entry.answer.length)}
                  className={`w-full rounded-lg border px-3 py-2 font-mono text-sm uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                    revealed
                      ? ok
                        ? "border-emerald-400 bg-emerald-50"
                        : "border-red-400 bg-red-50"
                      : "border-black/10 bg-white"
                  }`}
                />
                {revealed && (
                  <span className={ok ? "text-emerald-600" : "text-red-600"}>{ok ? "✓" : "✕"}</span>
                )}
              </div>
              {revealed && !ok && (
                <p className="mt-1 text-xs text-emerald-700">
                  {t("Correct:")} <span className="font-semibold">{entry.answer}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {revealed && <Explanation text={c.explanation} />}
    </div>
  );
}
